import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, assetSrc } from "@/context/AuthContext";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { fmtCurrencyPrecise } from "@/lib/format";
import { Pagination } from "@/components/Pagination";
import { MagnifyingGlass, Package, PencilSimple, Plus, X, Trash, Warning } from "@phosphor-icons/react";

const TH = "px-3 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const STATUS_LABEL = { A: "statusActive", D: "statusDisabled", H: "statusHidden" };
const STATUS_CLS = { A: "text-emerald-600", D: "text-muted-foreground", H: "text-amber-600" };
const NINPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary";
const flattenCats = (nodes, depth = 0, out = []) => { (nodes || []).forEach((c) => { out.push({ id: String(c.category_id), name: `${"— ".repeat(depth)}${c.name?.fr || c.name?.en || ""}` }); if (c.children) flattenCats(c.children, depth + 1, out); }); return out; };

export default function ProductsAdminPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category") || "";
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ name_fr: "", product_code: "", category: "", price: "", quantity: "", status: "A", with_similar: true });
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/products/${delTarget.product_id}`);
      toast.success(t.productDeleted);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t.deleteFailed); } finally { setDeleting(false); }
  };

  useEffect(() => { axios.get(`${API}/shop/categories`).then((r) => setCats(flattenCats(r.data))).catch(() => {}); }, []);
  const createProduct = async () => {
    if (!nf.name_fr.trim() || !nf.product_code.trim()) { toast.error(`${t.name} / ${t.productCode} *`); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/products`, {
        name_fr: nf.name_fr, product_code: nf.product_code,
        category_ids: nf.category ? [nf.category] : [], main_category: nf.category || null,
        price: parseFloat(nf.price) || 0, quantity: parseInt(nf.quantity, 10) || 0, status: nf.status,
        with_similar: nf.with_similar,
      });
      toast.success(t.productUpdated || "OK"); navigate(`/admin/products/${data.product_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setCreating(false); }
  };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/products`, { params: { q, category: categoryFilter || undefined, page, per_page: perPage } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [q, page, perPage, categoryFilter]);
  useEffect(() => { setPage(1); }, [q, perPage, categoryFilter]);
  const categoryName = cats.find((c) => c.id === categoryFilter)?.name?.replace(/^(—\s)+/, "") || categoryFilter;
  const clearCategory = () => { searchParams.delete("category"); setSearchParams(searchParams); };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 sm:px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.backOffice}</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">{t.productsManagement}</h1>
          </div>
          <button data-testid="new-product-button" onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
            <Plus size={16} weight="bold" /> {t.newProduct}
          </button>
        </div>

        <div className="mb-5 flex items-center border-2 border-border bg-card px-4 focus-within:border-primary transition-colors duration-200">
          <MagnifyingGlass size={20} weight="bold" className="text-muted-foreground" />
          <input data-testid="product-admin-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`${t.searchProducts} (${t.name}, ${t.productCode}, ID)`}
            className="w-full bg-transparent px-3 py-3.5 text-sm text-foreground outline-none placeholder-muted-foreground" />
          <span className="hidden text-xs text-muted-foreground sm:inline">{data.total} {t.products.toLowerCase()}</span>
        </div>

        {categoryFilter && (
          <div data-testid="category-filter-banner" className="mb-4 flex items-center justify-between gap-3 border-l-4 border-primary bg-primary/5 px-4 py-3">
            <div className="text-sm text-foreground">
              <span className="font-bold uppercase tracking-wider text-primary text-[11px]">{t.filteredByCategory}: </span>
              <span className="font-semibold">{categoryName}</span>
              <span className="ml-2 text-muted-foreground">({data.total})</span>
            </div>
            <button data-testid="clear-category-filter" onClick={clearCategory}
              className="inline-flex items-center gap-1.5 border border-border bg-card px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground transition-colors duration-200 hover:border-primary hover:text-primary">
              <X size={14} weight="bold" /> {t.clearFilter}
            </button>
          </div>
        )}

        <div className="mb-4 flex justify-end">
          <Pagination testid="products-pagination-top" page={page} perPage={perPage} total={data.total || 0} onPage={setPage} onPerPage={setPerPage} />
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          <table data-testid="products-table" className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left">
                <th className={TH}></th>
                <th className={TH}>{t.name} / {t.productCode}</th>
                <th className={`${TH} text-right`}>{t.price} ($)</th>
                <th className={`${TH} text-center`}>{t.stock}</th>
                <th className={TH}>{t.status}</th>
                <th className={`${TH} text-right`}>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t.loading}</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t.noResults}</td></tr>}
              {!loading && data.items.map((p) => (
                <tr key={p.id} data-testid={`admin-product-row-${p.product_id}`} className="border-b border-border hover:bg-muted/40 transition-colors duration-150">
                  <td className="px-3 py-2">
                    <div className="h-11 w-11 shrink-0 bg-muted">
                      {p.image ? <img src={assetSrc(p.image)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={18} /></div>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => navigate(`/admin/products/${p.product_id}`)} className="text-left font-medium text-primary hover:underline">{p.name[lang] || p.name.fr}</button>
                    <div className="font-mono text-[11px] text-muted-foreground">{p.product_code}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">{fmtCurrencyPrecise(p.price || 0, lang)}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{p.quantity || 0}</td>
                  <td className="px-3 py-2"><span className={`text-xs font-semibold ${STATUS_CLS[p.status] || ""}`}>{t[STATUS_LABEL[p.status] || "statusActive"]}</span></td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button data-testid={`edit-product-${p.product_id}`} onClick={() => navigate(`/admin/products/${p.product_id}`)}
                        className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200">
                        <PencilSimple size={14} weight="bold" /> {t.edit}
                      </button>
                      <button data-testid={`delete-product-${p.product_id}`} onClick={() => setDelTarget(p)} title={t.deleteProduct}
                        className="inline-flex items-center border border-red-400 p-1.5 text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-200">
                        <Trash size={15} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <Pagination testid="products-pagination-bottom" page={page} perPage={perPage} total={data.total || 0} onPage={setPage} onPerPage={setPerPage} />
        </div>
      </main>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="new-product-modal">
          <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-black tracking-tight text-foreground">{t.newProduct}</h2>
              <button onClick={() => setShowNew(false)} className="text-muted-foreground hover:text-foreground"><X size={20} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="np-name" className={NINPUT} placeholder={`${t.name} (FR) *`} value={nf.name_fr} onChange={(e) => setNf({ ...nf, name_fr: e.target.value })} />
              <input data-testid="np-code" className={`${NINPUT} font-mono`} placeholder={`${t.productCode} *`} value={nf.product_code} onChange={(e) => setNf({ ...nf, product_code: e.target.value })} />
              <select data-testid="np-category" className={NINPUT} value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })}>
                <option value="">— {t.categories} —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="np-price" type="number" step="0.01" min="0" className={NINPUT} placeholder={`${t.price} ($)`} value={nf.price} onChange={(e) => setNf({ ...nf, price: e.target.value })} />
                <input data-testid="np-quantity" type="number" min="0" className={NINPUT} placeholder={t.stock} value={nf.quantity} onChange={(e) => setNf({ ...nf, quantity: e.target.value })} />
              </div>
              <select data-testid="np-status" className={NINPUT} value={nf.status} onChange={(e) => setNf({ ...nf, status: e.target.value })}>
                <option value="A">{t.statusActive}</option>
                <option value="H">{t.statusHidden}</option>
                <option value="D">{t.statusDisabled}</option>
              </select>
              <label className="flex items-center gap-2.5 pt-1 cursor-pointer select-none">
                <input data-testid="np-with-similar" type="checkbox" checked={nf.with_similar} onChange={(e) => setNf({ ...nf, with_similar: e.target.checked })} className="h-4 w-4 accent-primary" />
                <span className="text-sm text-foreground">{t.withSimilar}</span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setShowNew(false)} className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted">{t.cancel}</button>
              <button data-testid="np-create" onClick={createProduct} disabled={creating} className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"><Plus size={14} weight="bold" /> {t.createProduct}</button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-product-modal">
          <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"><Warning size={22} weight="bold" /></div>
              <div>
                <h2 className="font-display text-lg font-black tracking-tight text-foreground">{t.deleteProductTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.deleteProductWarning}</p>
              </div>
            </div>
            <div className="mb-5 border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-sm font-semibold text-foreground">{delTarget.name?.[lang] || delTarget.name?.fr}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{delTarget.product_code}</div>
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="delete-product-cancel" onClick={() => setDelTarget(null)} disabled={deleting}
                className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted disabled:opacity-60">{t.cancel}</button>
              <button data-testid="delete-product-confirm" onClick={confirmDelete} disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60">
                <Trash size={14} weight="bold" /> {t.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
